import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import z from "zod";

import { isSuperadminRole } from "@/auth/shared";
import { Alert } from "@/components/base/alert";
import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Link } from "@/components/base/link";
import { Modal, ModalHeading } from "@/components/base/modal";
import { TextField } from "@/components/base/text-field";
import { addToast } from "@/components/base/toast";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";

export const Route = createFileRoute("/admin/reconcile-profiles")({
  loader: async ({ context: { queryClient, ...context } }) => {
    const viewer = context.viewer;

    if (!viewer || !isSuperadminRole(viewer.role)) {
      throw redirect({ to: "/not-found" });
    }
  },
  component: RouteComponent,
});

// ── Stat Card ──

function StatCard({
  label,
  value,
  color = "default",
}: {
  label: string;
  value: number;
  color?: "default" | "warning" | "success";
}) {
  const colorClasses = {
    default: "bg-gray-50 border-gray-200",
    warning: "bg-amber-50 border-amber-200",
    success: "bg-green-50 border-green-200",
  };

  return (
    <div className={`rounded-lg border p-4 ${colorClasses[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
    </div>
  );
}

// ── Stats Section ──

function StatsSection() {
  const trpc = useTRPC();
  const { data: stats } = useSuspenseQuery(
    trpc.reconcileProfiles.getStats.queryOptions(),
  );

  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <StatCard
        label="Multi-self total"
        value={stats.multiSelfTotal}
        color={stats.multiSelfTotal > 0 ? "warning" : "success"}
      />
      <StatCard
        label="Auto-fixable"
        value={stats.autoFixable}
        color={stats.autoFixable > 0 ? "warning" : "success"}
      />
      <StatCard
        label="Needs review"
        value={stats.needsReview}
        color={stats.needsReview > 0 ? "warning" : "success"}
      />
      <StatCard
        label="Missing junctions"
        value={stats.missingJunctions}
        color={stats.missingJunctions > 0 ? "warning" : "success"}
      />
    </div>
  );
}

// ── Auto-fix Preview / Apply ──

function AutoFixPreview() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [showPreview, setShowPreview] = useState(false);

  const {
    data: preview,
    isLoading,
    refetch,
  } = useQuery({
    ...trpc.reconcileProfiles.previewAutoFix.queryOptions(),
    enabled: showPreview,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.reconcileProfiles.getStats.queryOptions().queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: trpc.reconcileProfiles.listMultiSelfUsers.queryOptions._def,
    });
    queryClient.invalidateQueries({
      queryKey: trpc.reconcileProfiles.previewAutoFix.queryOptions().queryKey,
    });
  };

  const {
    mutate: applyAutoFix,
    isPending: isApplying,
    failureReason: applyError,
  } = useMutation({
    ...trpc.reconcileProfiles.applyAutoFix.mutationOptions(),
    onSuccess: (data) => {
      addToast({
        variant: "success",
        title: "Auto-fix applied",
        description: `Fixed ${data.fixed} user(s): ${data.guardians} changed to guardian.`,
      });
      setShowPreview(false);
      invalidateAll();
    },
  });

  const handlePreview = () => {
    setShowPreview(true);
    refetch();
  };

  const handleApply = () => {
    if (!preview?.items.length) return;
    applyAutoFix({ userIds: preview.items.map((i) => i.userId) });
  };

  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-semibold">Auto-fix Multi-Self</h3>
      {applyError && (
        <Alert color="error" title="Error" description={applyError.message} />
      )}

      {!showPreview && (
        <div>
          <Button color="primary" onPress={handlePreview}>
            Preview auto-fix
          </Button>
        </div>
      )}

      {isLoading && (
        <div className="text-sm text-gray-500">Loading preview...</div>
      )}

      {preview &&
        showPreview &&
        (preview.items.length === 0 ? (
          <div className="text-sm text-gray-500">
            No auto-fixable cases found.
          </div>
        ) : (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="font-semibold text-blue-800">
                Preview: {preview.items.length} user(s) will be fixed
              </h4>
              <div className="flex gap-2">
                <Button
                  variant="text"
                  size="sm"
                  onPress={() => setShowPreview(false)}
                >
                  Dismiss
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  onPress={handleApply}
                  isDisabled={isApplying}
                >
                  {isApplying ? "Applying..." : "Apply all"}
                </Button>
              </div>
            </div>
            <div className="flex max-h-96 flex-col gap-2 overflow-y-auto">
              {preview.items.map((item) => (
                <div
                  key={item.userId}
                  className="rounded border border-blue-100 bg-white p-3 text-sm"
                >
                  <div className="font-medium">
                    <Link
                      to="/admin/user/$userId"
                      params={{ userId: item.userId }}
                      className="text-blue-700 underline"
                    >
                      {item.userName}
                    </Link>
                    <span className="ml-2 text-gray-500">{item.userEmail}</span>
                  </div>
                  <div className="mt-1 text-gray-600">
                    Keep:{" "}
                    <span className="font-medium text-green-700">
                      #{item.keptProfileId} {item.keptProfileName}
                    </span>
                    {item.keptTournamentCount > 0 && (
                      <span className="ml-1 text-gray-500">
                        ({item.keptTournamentCount} tournaments)
                      </span>
                    )}
                  </div>
                  {item.others.map((other) => (
                    <div key={other.profileId} className="mt-1 text-gray-600">
                      <span className="font-medium text-amber-700">
                        → Guardian
                      </span>
                      {": "}#{other.profileId} {other.name}
                      {other.birthdate && (
                        <span className="ml-1 text-gray-400">
                          (DOB: {other.birthdate})
                        </span>
                      )}
                      {other.tournamentCount > 0 && (
                        <span className="ml-1 text-gray-500">
                          ({other.tournamentCount} tournaments)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}

// ── Missing Junctions Preview / Apply ──

function MissingJunctionsPreview() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [showPreview, setShowPreview] = useState(false);

  const {
    data: preview,
    isLoading,
    refetch,
  } = useQuery({
    ...trpc.reconcileProfiles.previewMissingJunctions.queryOptions(),
    enabled: showPreview,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.reconcileProfiles.getStats.queryOptions().queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: trpc.reconcileProfiles.listMissingJunctions.queryOptions._def,
    });
    queryClient.invalidateQueries({
      queryKey:
        trpc.reconcileProfiles.previewMissingJunctions.queryOptions().queryKey,
    });
  };

  const {
    mutate: applyMissingJunctions,
    isPending: isApplying,
    failureReason: applyError,
  } = useMutation({
    ...trpc.reconcileProfiles.applyMissingJunctions.mutationOptions(),
    onSuccess: (data) => {
      addToast({
        variant: "success",
        title: "Junctions created",
        description: `Created ${data.created} junction(s).`,
      });
      setShowPreview(false);
      invalidateAll();
    },
  });

  const handlePreview = () => {
    setShowPreview(true);
    refetch();
  };

  const handleApply = () => {
    if (!preview?.items.length) return;
    applyMissingJunctions({
      profileIds: preview.items.map((i) => i.profileId),
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-semibold">Create Missing Junctions</h3>
      {applyError && (
        <Alert color="error" title="Error" description={applyError.message} />
      )}

      {!showPreview && (
        <div>
          <Button color="primary" variant="outline" onPress={handlePreview}>
            Preview missing junctions
          </Button>
        </div>
      )}

      {isLoading && (
        <div className="text-sm text-gray-500">Loading preview...</div>
      )}

      {preview &&
        showPreview &&
        (preview.items.length === 0 ? (
          <div className="text-sm text-gray-500">
            No missing junctions found.
          </div>
        ) : (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="font-semibold text-blue-800">
                Preview: {preview.items.length} junction(s) will be created
              </h4>
              <div className="flex gap-2">
                <Button
                  variant="text"
                  size="sm"
                  onPress={() => setShowPreview(false)}
                >
                  Dismiss
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  onPress={handleApply}
                  isDisabled={isApplying}
                >
                  {isApplying ? "Creating..." : "Create all"}
                </Button>
              </div>
            </div>
            <div className="flex max-h-96 flex-col gap-1 overflow-y-auto">
              {preview.items.map((item) => (
                <div
                  key={item.profileId}
                  className="flex items-center justify-between rounded border border-blue-100 bg-white px-3 py-2 text-sm"
                >
                  <div>
                    <span className="font-medium">
                      #{item.profileId} {item.profileName}
                    </span>
                    <span className="mx-1 text-gray-400">&rarr;</span>
                    <Link
                      to="/admin/user/$userId"
                      params={{ userId: item.userId }}
                      className="text-blue-700 underline"
                    >
                      {item.userName}
                    </Link>
                  </div>
                  <span
                    className={
                      item.role === "self" ? "text-green-700" : "text-amber-700"
                    }
                  >
                    {item.role}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}

// ── Split to New Account Modal ──

function SplitToNewAccountModal({
  isOpen,
  onOpenChange,
  userId,
  profileId,
  profileName,
  onSuccess,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  profileId: number;
  profileName: string;
  onSuccess: () => void;
}) {
  const trpc = useTRPC();

  const { mutate, isPending, failureReason } = useMutation({
    ...trpc.reconcileProfiles.splitToNewAccount.mutationOptions(),
    onSuccess: (data) => {
      addToast({
        variant: "success",
        title: "Profile split",
        description: `New account created for ${data.email}.`,
      });
      onOpenChange(false);
      onSuccess();
    },
  });

  const splitSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
  });

  const form = useAppForm({
    defaultValues: { email: "" },
    validators: { onMount: splitSchema, onChange: splitSchema },
    onSubmit: ({ value }) => {
      mutate({ userId, profileId, email: value.email });
    },
  });

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="flex flex-col gap-4 p-6">
        <ModalHeading size="xs">Split to New Account</ModalHeading>
        <p className="text-sm text-gray-600">
          Enter an email address for <strong>{profileName}</strong>&apos;s new
          account. They&apos;ll need to reset their password to log in.
        </p>
        {failureReason && (
          <Alert
            color="error"
            title="Error"
            description={failureReason.message}
          />
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <form.AppField
            name="email"
            children={(field) => (
              <field.Text
                field={field}
                label="Email"
                type="email"
                isRequired
                autoFocus
              />
            )}
          />
          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>
              <form.SubmitButton isDisabled={isPending}>
                {isPending ? "Creating..." : "Create Account"}
              </form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}

// ── Move to Existing User Modal ──

function MoveToExistingUserModal({
  isOpen,
  onOpenChange,
  fromUserId,
  profileId,
  profileName,
  onSuccess,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fromUserId: string;
  profileId: number;
  profileName: string;
  onSuccess: () => void;
}) {
  const trpc = useTRPC();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const { data: searchResults, isLoading: isSearching } = useQuery({
    ...trpc.reconcileProfiles.searchUsers.queryOptions({
      query: searchQuery,
    }),
    enabled: searchQuery.length >= 2,
  });

  const { mutate, isPending } = useMutation({
    ...trpc.reconcileProfiles.moveToExistingUser.mutationOptions(),
    onSuccess: () => {
      addToast({
        variant: "success",
        title: "Profile moved",
        description: `${profileName} moved to another user.`,
      });
      onOpenChange(false);
      onSuccess();
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSelectedUserId(null);
    }
  }, [isOpen]);

  const handleMove = () => {
    if (!selectedUserId) return;
    mutate({ fromUserId, profileId, toUserId: selectedUserId });
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className="flex flex-col gap-4 p-6">
        <ModalHeading size="xs">Move to Existing User</ModalHeading>
        <p className="text-sm text-gray-600">
          Search for a user to move <strong>{profileName}</strong> to.
        </p>
        <TextField
          label="Search"
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(value) => {
            setSearchQuery(value);
            setSelectedUserId(null);
          }}
        />
        {isSearching && (
          <div className="text-sm text-gray-500">Searching...</div>
        )}
        {searchResults && searchResults.length > 0 && (
          <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded border border-gray-200">
            {searchResults
              .filter((u) => u.id !== fromUserId)
              .map((user) => (
                <Button
                  key={user.id}
                  variant="text"
                  className={`flex flex-col items-start px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    selectedUserId === user.id
                      ? "border-l-2 border-blue-500 bg-blue-50"
                      : ""
                  }`}
                  onPress={() => setSelectedUserId(user.id)}
                >
                  <span className="font-medium">{user.name}</span>
                  <span className="text-xs text-gray-500">{user.email}</span>
                </Button>
              ))}
          </div>
        )}
        {searchResults &&
          searchResults.length === 0 &&
          searchQuery.length >= 2 && (
            <div className="text-sm text-gray-500">No users found.</div>
          )}
        <div className="flex justify-end gap-2">
          <Button onPress={() => onOpenChange(false)}>Cancel</Button>
          <Button
            color="primary"
            isDisabled={!selectedUserId || isPending}
            onPress={handleMove}
          >
            {isPending ? "Moving..." : "Move Profile"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Multi-Self Review List ──

function MultiSelfReviewList() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [splitTarget, setSplitTarget] = useState<{
    userId: string;
    profileId: number;
    profileName: string;
  } | null>(null);
  const [moveTarget, setMoveTarget] = useState<{
    userId: string;
    profileId: number;
    profileName: string;
  } | null>(null);

  const { data } = useSuspenseQuery(
    trpc.reconcileProfiles.listMultiSelfUsers.queryOptions({
      filter: "review",
      page,
      size: 10,
    }),
  );

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.reconcileProfiles.getStats.queryOptions().queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: trpc.reconcileProfiles.listMultiSelfUsers.queryOptions._def,
    });
  };

  const { mutate: mergeIntoProfile, isPending: isMerging } = useMutation({
    ...trpc.reconcileProfiles.mergeIntoProfile.mutationOptions(),
    onSuccess: () => {
      addToast({ variant: "success", title: "Merged" });
      invalidateAll();
    },
  });

  const { mutate: demoteToGuardian, isPending: isDemoting } = useMutation({
    ...trpc.reconcileProfiles.demoteToGuardian.mutationOptions(),
    onSuccess: () => {
      addToast({ variant: "success", title: "Changed to guardian" });
      invalidateAll();
    },
  });

  const isPending = isMerging || isDemoting;

  if (data.data.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        No multi-self cases needing review.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">
        Multi-Self Review ({data.pageInfo.totalItems})
      </h2>
      <div className="flex flex-col gap-3">
        {data.data.map((row) => (
          <div
            key={row.userId}
            className="rounded-lg border border-gray-200 p-4"
          >
            <div className="mb-3">
              <Link
                to="/admin/user/$userId"
                params={{ userId: row.userId }}
                className="font-medium text-blue-700 underline"
              >
                {row.userName}
              </Link>
              <span className="ml-2 text-sm text-gray-500">
                {row.userEmail}
              </span>
            </div>
            <div className="flex flex-col gap-3">
              {row.profiles.map((profile) => {
                const otherProfiles = row.profiles.filter(
                  (p) => p.profileId !== profile.profileId,
                );

                return (
                  <div
                    key={profile.profileId}
                    className="rounded border border-gray-100 bg-gray-50 p-3"
                  >
                    <div className="mb-2 text-sm">
                      <span className="font-medium">
                        #{profile.profileId} {profile.name}
                      </span>
                      {profile.birthdate && (
                        <span className="ml-2 text-gray-400">
                          DOB: {profile.birthdate}
                        </span>
                      )}
                      {profile.tournamentCount > 0 && (
                        <span className="ml-2 text-amber-600">
                          {profile.tournamentCount} tournaments
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {otherProfiles.map((other) => (
                        <Button
                          key={`merge-${other.profileId}`}
                          variant="outline"
                          size="sm"
                          color="secondary"
                          isDisabled={isPending}
                          onPress={() =>
                            mergeIntoProfile({
                              keepProfileId: other.profileId,
                              deleteProfileId: profile.profileId,
                            })
                          }
                        >
                          Merge into #{other.profileId}
                        </Button>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        color="warning"
                        isDisabled={isPending}
                        onPress={() =>
                          demoteToGuardian({
                            userId: row.userId,
                            profileId: profile.profileId,
                          })
                        }
                      >
                        Guardian
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        isDisabled={isPending}
                        onPress={() =>
                          setSplitTarget({
                            userId: row.userId,
                            profileId: profile.profileId,
                            profileName: profile.name,
                          })
                        }
                      >
                        Split
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        isDisabled={isPending}
                        onPress={() =>
                          setMoveTarget({
                            userId: row.userId,
                            profileId: profile.profileId,
                            profileName: profile.name,
                          })
                        }
                      >
                        Move
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {data.pageInfo.totalPages > 1 && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            isDisabled={page === 1}
            onPress={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {page} of {data.pageInfo.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            isDisabled={page >= data.pageInfo.totalPages}
            onPress={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {splitTarget && (
        <SplitToNewAccountModal
          isOpen={!!splitTarget}
          onOpenChange={(open) => {
            if (!open) setSplitTarget(null);
          }}
          userId={splitTarget.userId}
          profileId={splitTarget.profileId}
          profileName={splitTarget.profileName}
          onSuccess={invalidateAll}
        />
      )}

      {moveTarget && (
        <MoveToExistingUserModal
          isOpen={!!moveTarget}
          onOpenChange={(open) => {
            if (!open) setMoveTarget(null);
          }}
          fromUserId={moveTarget.userId}
          profileId={moveTarget.profileId}
          profileName={moveTarget.profileName}
          onSuccess={invalidateAll}
        />
      )}
    </div>
  );
}

// ── Missing Junctions List ──

function MissingJunctionsList() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);

  const { data } = useSuspenseQuery(
    trpc.reconcileProfiles.listMissingJunctions.queryOptions({
      page,
      size: 10,
    }),
  );

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.reconcileProfiles.getStats.queryOptions().queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: trpc.reconcileProfiles.listMissingJunctions.queryOptions._def,
    });
  };

  const { mutate: createJunction, isPending } = useMutation({
    ...trpc.reconcileProfiles.createMissingJunction.mutationOptions(),
    onSuccess: (result) => {
      if (result.created) {
        addToast({
          variant: "success",
          title: "Junction created",
          description: `Created as ${result.role}.`,
        });
      }
      invalidateAll();
    },
  });

  if (data.data.length === 0) {
    return (
      <div className="text-sm text-gray-500">
        No profiles with missing junctions.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold">
        Missing Junctions ({data.pageInfo.totalItems})
      </h2>
      <div className="flex flex-col gap-2">
        {data.data.map((row) => (
          <div
            key={row.profileId}
            className="flex items-center justify-between rounded-lg border border-gray-200 p-3"
          >
            <div className="flex flex-col">
              <div className="text-sm">
                Profile #{row.profileId}: <strong>{row.profileName}</strong>
              </div>
              <div className="text-xs text-gray-500">
                User:{" "}
                <Link
                  to="/admin/user/$userId"
                  params={{ userId: row.userId }}
                  className="text-blue-700 underline"
                >
                  {row.userName}
                </Link>
                {" — suggested: "}
                <span
                  className={
                    row.suggestedRole === "self"
                      ? "text-green-700"
                      : "text-amber-700"
                  }
                >
                  {row.suggestedRole}
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              isDisabled={isPending}
              onPress={() => createJunction({ profileId: row.profileId })}
            >
              Create
            </Button>
          </div>
        ))}
      </div>
      {data.pageInfo.totalPages > 1 && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            isDisabled={page === 1}
            onPress={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {page} of {data.pageInfo.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            isDisabled={page >= data.pageInfo.totalPages}
            onPress={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Main Route Component ──

function RouteComponent() {
  return (
    <DefaultLayout
      classNames={{
        content: "flex flex-col gap-8 max-w-4xl px-3 py-12 mx-auto",
      }}
    >
      <h1 className="text-2xl font-bold">Reconcile Profiles</h1>
      <StatsSection />
      <div className="flex flex-col gap-6">
        <h2 className="text-lg font-semibold">Batch Actions</h2>
        <AutoFixPreview />
        <MissingJunctionsPreview />
      </div>
      <MultiSelfReviewList />
      <MissingJunctionsList />
    </DefaultLayout>
  );
}
